import type { IRepository } from '../interfaces/IRepository';
import { db } from '../db';
import { retryableQuery } from '../functions/retryableQuery';
import { eq, and } from 'drizzle-orm';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { DATA_API_MESSAGES, HTTP_STATUS_NAMES } from '@shared/constants';


/**
 * BaseRepository<T>
 * Abstract class for all domain repositories.
 * Enforces a consistent contract for data access and CRUD operations.
 * All repositories must extend this class.
 */
/**
 * @internal
 */
export abstract class BaseRepository<T> implements IRepository<T> {
  /**
   * The Drizzle table object for the entity
   */
  protected abstract table: any;

  /**
   * Get a single entity by its identifier (must be implemented by subclass)
   */
  async getById(id: string): AsyncResult<T, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.select().from(this.table).where(eq(this.table.id, id))) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from getById: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (rows.length === 0) {
              return err(convertToAppError(`No record found with id: ${id}`, DATA_API_MESSAGES.ERRORS.ID_REQUIRED, HTTP_STATUS_NAMES.NOT_FOUND));
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Get all entities (generic implementation)
   * 
   * TO-DO: Add pagination and sorting
   */
  async getAll(): AsyncResult<T[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.select().from(this.table)) as T[]
        );

        return queryResult.match({
          ok: (rows: T[]) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Create a new entity
   */
  async create(data: Partial<T>): AsyncResult<T, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.insert(this.table).values(data).returning()) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from create: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (rows.length === 0) {
              return err(convertToAppError("Insert succeeded but no record returned.", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.NOT_FOUND));
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Create if not exists with automatic field inference
   */
  async createIfNotExists(data: Partial<T>): AsyncResult<T, AppError>;
  /**
   * Create if not exists with explicit unique fields
   */
  async createIfNotExists(data: Partial<T>, uniqueFields: (keyof T)[]): AsyncResult<T, AppError>;
  async createIfNotExists(data: Partial<T>, uniqueFields?: (keyof T)[]): AsyncResult<T, AppError> {
    // If no unique fields provided, infer from data keys
    const fieldsToCheck = uniqueFields || (Object.keys(data) as (keyof T)[]);

    const searchFields: Record<string, any> = {};
    for (const field of fieldsToCheck) {
      if (data[field] !== undefined) {
        searchFields[field as string] = data[field];
      }
    }

    const existingResult = await this.findByFields(searchFields);

    if (existingResult.isErr) {
      return err(existingResult.getFail())
    }

    const existing = existingResult.getSuccess();

    if (existing && existing.length > 0) {
      return ok(existing[0]);
    }

    const result = await this.create(data);

    if (result.isErr) {
      return err(result.getFail())
    }

    return ok(result.getSuccess());
  }

  /**
   * Update an existing entity (must be implemented by subclass)
   */
  async update(id: string, data: Partial<T>): AsyncResult<T, AppError> {
    return db.match({
      ok: async (database) => {
        const updateData = { ...data };

        // Handle automatic timestamping
        if ('date_modified' in this.table) {
          (updateData as any).date_modified = new Date();
        } else if ('updatedAt' in this.table) {
          (updateData as any).updatedAt = new Date();
        } else if ('updated_at' in this.table) {
          (updateData as any).updated_at = new Date();
        }

        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.update(this.table).set(updateData).where(eq(this.table.id, id)).returning()) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from update: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (!rows[0]) {
              return err(convertToAppError(`No record found or updated`, DATA_API_MESSAGES.ERRORS.ID_REQUIRED, HTTP_STATUS_NAMES.NOT_FOUND));
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Delete an entity (generic implementation, can be overridden)
   */
  async delete(id: string): AsyncResult<boolean, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.delete(this.table).where(eq(this.table.id, id)).returning()) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from delete: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (!rows[0]) {
              return err(convertToAppError(`No record found or deleted`, DATA_API_MESSAGES.ERRORS.ID_REQUIRED, HTTP_STATUS_NAMES.NOT_FOUND));
            }

            return ok(true);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Find a single entity by a specific field
   */
  async findByField(field: string, value: any): AsyncResult<T | null, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.select().from(this.table).where(eq(this.table[field], value))) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from findByField: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            if (!rows[0]) {
              return err(convertToAppError(`No record found`, DATA_API_MESSAGES.ERRORS.ID_REQUIRED, HTTP_STATUS_NAMES.NOT_FOUND));
            }

            return ok(rows[0] || null);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Find entities by multiple fields
   */
  async findByFields(fields: Record<string, any>): AsyncResult<T[], AppError> {
    return db.match({
      ok: async (database) => {
        const conditions = Object.entries(fields).map(([key, value]) =>
          eq(this.table[key], value)
        );

        const queryResult = await retryableQuery<T[]>(async () =>
          (await database.select().from(this.table).where(and(...conditions))) as T[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError("Unexpected result from findByFields: not an array", DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER, HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

} 